import { useEffect, useMemo, useState } from 'react';
import { Button, ListBox, ListBoxItem, ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import PROCESSING from 'components/cmp_processing/cmp_processing';
import ICON from 'components/cmp_icon/cmp_icon';
import { INPUT } from 'components/cmp_form/cmp_form';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const REVIEW_STATUSES = new Set(['PENDING', 'ACCEPTED', 'REJECTED']);
const REVIEW_STATUS_ORDER = {ACCEPTED: 0, PENDING: 1, REJECTED: 2};

function HIGHLIGHTED_TEXT({ text, term }) {
  if (typeof text !== 'string' || !term) return text;

  const parts = text.split(new RegExp(`(${escapeRegExp(term)})`, 'gi'));
  return parts.map((part, index) => (
    part.toLowerCase() === term.toLowerCase()
      ? <span className='tab-locations__highlight' key={index}>{part}</span>
      : part
  ));
}

const getLocationValue = (location) => (
  typeof location === 'string'
    ? location
    : location?.address || location?.address1 || location?.street ||
      location?.location || location?.name
);

const getLocationTitle = (location) => (
  location?.is_extracted
    ? location.address || location.address1 || location.street || location.location
    : location?.location
);

const getLocationIdentity = (location) => (
  String(location?.analysis_location || getLocationValue(location) || '').trim().toLowerCase().replace(/\s+/g, ' ')
);

const isVerifiedLocation = (location) => (
  location?.latitude != null &&
  location?.longitude != null &&
  location?.geocodeConfidence != null
);

const getReviewStatus = (location) => {
  if (location?.review_status === 'REJECTED') return 'REJECTED';
  if (location?.review_status === 'PENDING') return 'PENDING';
  if (location?.review_status === 'ACCEPTED') {
    return isVerifiedLocation(location) ? 'ACCEPTED' : 'PENDING';
  }
  return location?.is_extracted === true && isVerifiedLocation(location) ? 'ACCEPTED' : 'PENDING';
};

const getLocationKey = (location, index) => (
  getLocationIdentity(location) || `location-${index}`
);

const formatConfidence = (value) => (
  value == null || value === '' || !Number.isFinite(Number(value))
    ? '-'
    : Number(value).toFixed(2)
);

const formatCountry = (country) => {
  if (country === 'CA') return 'Canada';
  if (country === 'UK') return 'United Kingdom';
  return country;
};

const getVerificationAddress = (location) => (
  [
    getLocationTitle(location),
    location?.city,
    location?.province || location?.state,
    location?.postcode || location?.postal
  ].filter(Boolean).join(', ')
);

export default function TAB_LOCATIONS({ analysis, loading, error, providerData, updateLocations }) {
  const locations = useMemo(() => {
    const matches = analysis?.location_matches || [];
    const existing = providerData?.extractions?.locations || [];
    const existingByIdentity = new Map(
      existing
        .map((location) => [getLocationIdentity(location), location])
        .filter(([identity]) => identity)
    );
    const locationsByIdentity = new Map();

    matches.forEach((location) => {
      const existingLocation = existingByIdentity.get(getLocationIdentity(location));
      locationsByIdentity.set(getLocationIdentity(location), {
        ...existingLocation,
        ...location,
        location: existingLocation?.location || location.location,
        latitude: existingLocation?.latitude ?? existingLocation?.lat ?? location.latitude ?? location.lat,
        longitude: existingLocation?.longitude ?? existingLocation?.lon ?? location.longitude ?? location.lon,
        is_extracted: Boolean(existingLocation) || location.is_extracted,
        review_status: getReviewStatus({
          ...existingLocation,
          ...location,
          is_extracted: Boolean(existingLocation) || location.is_extracted
        })
      });
    });

    existing.forEach((location) => {
      const locationValue = getLocationValue(location);
      if (!locationValue) return;

      const identity = getLocationIdentity(location);
      if (!locationsByIdentity.has(identity)) {
        locationsByIdentity.set(identity, {
          ...location,
          location: locationValue,
          latitude: location.latitude ?? location.lat,
          longitude: location.longitude ?? location.lon,
          is_extracted: true,
          review_status: getReviewStatus({...location, is_extracted: true}),
          evidence: []
        });
      }
    });

    return [...locationsByIdentity.values()].sort((first, second) => (
      REVIEW_STATUS_ORDER[getReviewStatus(first)] - REVIEW_STATUS_ORDER[getReviewStatus(second)] ||
      Number(isVerifiedLocation(second)) - Number(isVerifiedLocation(first))
    ));
  }, [analysis, providerData]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [editingLocation, setEditingLocation] = useState(false);
  const [verificationAddress, setVerificationAddress] = useState('');
  const [verificationProcessing, setVerificationProcessing] = useState('inactive');
  const [verificationError, setVerificationError] = useState('');
  const [isAddingLocation, setIsAddingLocation] = useState(false);

  const selectedLocation = locations.find((location, index) => (
    getLocationKey(location, index) === selectedLocationId
  ));

  useEffect(() => {
    setSelectedLocationId((currentSelectedId) => {
      if (!locations.length) return null;
      if (currentSelectedId && locations.some((location, index) => (
        getLocationKey(location, index) === currentSelectedId
      ))) {
        return currentSelectedId;
      }
      return getLocationKey(locations[0], 0);
    });
  }, [locations]);

  const updateReviewStatus = (reviewStatus) => {
    const currentIndex = locations.findIndex((location, index) => (
      getLocationKey(location, index) === selectedLocationId
    ));
    const nextIndex = currentIndex >= 0 && locations.length > 1
      ? (currentIndex + 1) % locations.length
      : currentIndex;
    const updatedLocations = locations.map((location, index) => (
      getLocationKey(location, index) === selectedLocationId
        ? {...location, review_status: reviewStatus}
        : location
    ));
    updateLocations?.(updatedLocations);
    if (nextIndex >= 0) {
      setSelectedLocationId(getLocationKey(locations[nextIndex], nextIndex));
    }
  };

  const startVerification = () => {
    setVerificationAddress(getVerificationAddress(selectedLocation));
    setVerificationError('');
    setEditingLocation(true);
  };

  const cancelVerification = () => {
    setEditingLocation(false);
    setVerificationAddress('');
    setVerificationError('');
    setVerificationProcessing('inactive');
  };

  const restoreLocationView = () => {
    const currentIndex = locations.findIndex((location, index) => (
      getLocationKey(location, index) === selectedLocationId
    ));
    const nextIndex = currentIndex >= 0 && locations.length > 1
      ? (currentIndex + 1) % locations.length
      : 0;
    setIsAddingLocation(false);
    setVerificationAddress('');
    setVerificationError('');
    setVerificationProcessing('inactive');
    setSelectedLocationId(locations.length ? getLocationKey(locations[nextIndex], nextIndex) : null);
  };

  const startAddingLocation = () => {
    cancelVerification();
    setIsAddingLocation(true);
    setVerificationAddress('');
  };

  const verifyNewLocation = async () => {
    const address = verificationAddress.trim();
    if (!address) {
      setVerificationError('Enter an address to verify.');
      return;
    }

    setVerificationError('');
    setVerificationProcessing('processing');

    try {
      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(address)}&limit=1&format=json&apiKey=${process.env.REACT_APP_GEOAPIFY_KEY}`
      );
      if (!response.ok) {
        throw new Error(`Geoapify request failed with status ${response.status}`);
      }

      const result = await response.json();
      const geocode = result.results?.[0];
      if (!geocode || geocode.lat == null || geocode.lon == null) {
        throw new Error('Geoapify returned no usable location result');
      }

      updateLocations?.([
        ...locations,
        {
          analysis_location: address,
          location: address,
          address: geocode.address_line1 || address,
          city: geocode.city || geocode.county || null,
          state: geocode.state || geocode.state_code || null,
          country: geocode.country || null,
          postcode: geocode.postcode || null,
          latitude: geocode.lat,
          longitude: geocode.lon,
          geocodeConfidence: geocode.rank?.confidence ?? null,
          is_extracted: true,
          review_status: 'ACCEPTED',
          evidence: []
        }
      ]);
      restoreLocationView();
    } catch (verificationFailure) {
      console.error('New location verification failed:', verificationFailure);
      setVerificationError('Unable to verify this location.');
      setVerificationProcessing('error');
    }
  };

  const verifyLocation = async () => {
    const address = verificationAddress.trim();
    if (!address) {
      setVerificationError('Enter an address to verify.');
      return;
    }

    setVerificationError('');
    setVerificationProcessing('processing');

    try {
      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(address)}&limit=1&format=json&apiKey=${process.env.REACT_APP_GEOAPIFY_KEY}`
      );
      if (!response.ok) {
        throw new Error(`Geoapify request failed with status ${response.status}`);
      }

      const result = await response.json();
      const geocode = result.results?.[0];
      if (!geocode || geocode.lat == null || geocode.lon == null) {
        throw new Error('Geoapify returned no usable location result');
      }

      const updatedLocations = locations.map((location, index) => (
        getLocationKey(location, index) === selectedLocationId
          ? {
              ...location,
              analysis_location: location.analysis_location || location.location,
              location: address,
              address: geocode.address_line1 || location.address || address,
              city: geocode.city || geocode.county || location.city,
              state: geocode.state || geocode.state_code || location.state,
              country: geocode.country || location.country,
              postcode: geocode.postcode || location.postcode,
              latitude: geocode.lat,
              longitude: geocode.lon,
              geocodeConfidence: geocode.rank?.confidence ?? null,
              is_extracted: true,
              review_status: 'ACCEPTED'
            }
          : location
      ));

      updateLocations?.(updatedLocations);
      setEditingLocation(false);
      setVerificationProcessing('inactive');
    } catch (verificationFailure) {
      console.error('Location verification failed:', verificationFailure);
      setVerificationError('Unable to verify this location.');
      setVerificationProcessing('error');
    }
  };

  if (loading) return <p>Loading location analysis...</p>;
  if (error) return <p>Unable to load location analysis.</p>;

  return (
    <div className='tab-locations'>
      <nav className='tab-locations__nav'>
        <ListBox
          aria-label='Location matches'
          selectionMode='single'
          selectedKeys={selectedLocationId ? [selectedLocationId] : []}
          onSelectionChange={(keys) => {
            const selectedKey = Array.from(keys)[0];
            if (selectedKey) {
              cancelVerification();
              setSelectedLocationId(selectedKey);
            }
          }}
        >
          {locations.map((location, index) => (
            <ListBoxItem
              id={getLocationKey(location, index)}
              key={getLocationKey(location, index)}
              data-review-status={getReviewStatus(location)}
            >
              <div className='tab-locations__status-icon' aria-hidden='true' />
              <span className='tab-locations__item-label'>{location.location}</span>
            </ListBoxItem>
          ))}
        </ListBox>
        <button
          type='button'
          className='btn btn--secondary tab-locations__add-button'
          onClick={startAddingLocation}
        >
          Add a location
        </button>
      </nav>

      <div className='tab-locations__content'>
        {isAddingLocation ? (
          <div className='tab-locations__add'>
            <INPUT
              action={<ICON name='location' className='icon' alt='location icon'/>}
              actionLabel='Verify address'
              disabled={verificationProcessing === 'processing'}
              id='new-location'
              label='Address to verify'
              onAction={verifyNewLocation}
              onChange={(event) => setVerificationAddress(event.target.value)}
              value={verificationAddress}
            />
            <div className='tab-locations__add-actions'>
              <Button
                onPress={verifyNewLocation}
                isDisabled={verificationProcessing === 'processing' || !verificationAddress.trim()}
                className='btn btn--primary'
              >
                Verify
              </Button>
              <Button onPress={restoreLocationView} className='btn btn--secondary'>
                Cancel
              </Button>
            </div>
            {verificationError && (
              <div className='tab-locations__verification-error text--sm'>{verificationError}</div>
            )}
            <PROCESSING state={verificationProcessing}/>
          </div>
        ) : !locations.length ? (
          <div className='tab-locations__empty'>
            <p>No location matches</p>
          </div>
        ) : selectedLocation && (
          <section>
            <div className={`tab-locations__header${editingLocation ? ' is-verifying' : ' text--md medium'}`}>
              <div className='tab-locations__header-content'>
                {editingLocation ? (
                  <>
                    <INPUT
                      action={<ICON name='location' className='icon' alt='location icon'/>}
                      actionLabel='Verify address'
                      disabled={verificationProcessing === 'processing'}
                      id='location-verification'
                      label='Address to verify'
                      onAction={verifyLocation}
                      onChange={(event) => setVerificationAddress(event.target.value)}
                      value={verificationAddress}
                    />
                    <Button onPress={cancelVerification}>Cancel</Button>
                  </>
                ) : selectedLocation.is_extracted ? (
                  <>
                    <div>{getLocationTitle(selectedLocation)}</div>
                    <div className='tab-locations__subheader text--sm color--gray--900'>
                      {[ 
                        selectedLocation.city,
                        selectedLocation.province || selectedLocation.state,
                        formatCountry(selectedLocation.country)
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                    {(selectedLocation.latitude != null ||
                      selectedLocation.longitude != null ||
                      selectedLocation.geocodeConfidence != null) && (
                      <div className='tab-locations__coordinates text--sm'>
                        <div>
                          <span className='tab-locations__coordinate-label color--gray--500'>Confidence:</span>
                          <span className='color--gray--900'>{formatConfidence(selectedLocation.geocodeConfidence)}</span>
                        </div>
                        <div>
                          <span className='tab-locations__coordinate-label color--gray--500'>Latitude:</span>
                          <span className='color--gray--900'>{selectedLocation.latitude ?? '-'}</span>
                        </div>
                        <div>
                          <span className='tab-locations__coordinate-label color--gray--500'>Longitude:</span>
                          <span className='color--gray--900'>{selectedLocation.longitude ?? '-'}</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : selectedLocation.location}
              </div>
              {!editingLocation && (
                <ToggleButtonGroup
                  aria-label={`Review status for ${selectedLocation.location}`}
                  selectedKeys={new Set([selectedLocation.review_status])}
                  selectionMode='single'
                  onSelectionChange={(keys) => {
                    const selectedStatus = Array.from(keys)[0];
                    if (REVIEW_STATUSES.has(selectedStatus)) {
                      if (selectedStatus === 'ACCEPTED' && getReviewStatus(selectedLocation) !== 'ACCEPTED') {
                        startVerification();
                      } else {
                        updateReviewStatus(selectedStatus);
                      }
                    }
                  }}
                >
                  <ToggleButton id='REJECTED'>Rejected</ToggleButton>
                  <ToggleButton id='PENDING'>Pending</ToggleButton>
                  <ToggleButton id='ACCEPTED'>
                    {getReviewStatus(selectedLocation) === 'ACCEPTED' ? 'Accepted' : 'Verify'}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              {verificationError && (
                <div className='tab-locations__verification-error text--sm'>{verificationError}</div>
              )}
              <PROCESSING state={verificationProcessing}/>
            </div>
            <div className='tab-locations__evidence-list'>
            {selectedLocation.evidence?.length ? selectedLocation.evidence.map((evidence, index) => (
              <article className='tab-locations__evidence' key={`${evidence.page_url}-${index}`}>
                <div className='tab-locations__evidence-text'>
                  ...<HIGHLIGHTED_TEXT text={evidence.text} term={selectedLocation.location} />...
                </div>
                <a
                  className='tab-locations__page-link text--sm italic color--gray--500'
                  href={evidence.page_url}
                  target='_blank'
                  rel='noreferrer'
                >
                  {evidence.page_url}
                </a>
              </article>
            )) : <p>No evidence found for this location.</p>}
            </div>

          </section>
        )}
      </div>
    </div>
  );
}
